/**
 * PROJ-004: Browser Cleanup Utilities
 *
 * Functions to clean up browser storage, cache, and service workers.
 * Helps prevent stale data issues by clearing all client-side storage.
 */

/**
 * Clear all localStorage data
 */
export function clearLocalStorage(): void {
  try {
    localStorage.clear();
  } catch (error) {
    console.error('❌ Failed to clear localStorage:', error);
  }
}

/**
 * Clear all sessionStorage data
 */
export function clearSessionStorage(): void {
  try {
    sessionStorage.clear();
  } catch (error) {
    console.error('❌ Failed to clear sessionStorage:', error);
  }
}

/**
 * Delete all IndexedDB databases
 */
export async function clearIndexedDB(): Promise<void> {
  try {
    if (!('indexedDB' in window)) {
      console.warn('⚠️  IndexedDB not supported');
      return;
    }

    const databases = await indexedDB.databases();
    const deletePromises = databases.map((db) => {
      if (db.name) {
        return new Promise<void>((resolve, reject) => {
          const request = indexedDB.deleteDatabase(db.name!);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      }
      return Promise.resolve();
    });

    await Promise.all(deletePromises);
  } catch (error) {
    console.error('❌ Failed to clear IndexedDB:', error);
  }
}

/**
 * Unregister all service workers
 */
export async function unregisterServiceWorkers(): Promise<void> {
  try {
    if (!('serviceWorker' in navigator)) {
      console.warn('⚠️  Service Workers not supported');
      return;
    }

    const registrations = await navigator.serviceWorker.getRegistrations();
    const unregisterPromises = registrations.map((registration) =>
      registration.unregister()
    );

    await Promise.all(unregisterPromises);
  } catch (error) {
    console.error('❌ Failed to unregister Service Workers:', error);
  }
}

/**
 * Clear all Cache API caches
 */
export async function clearCaches(): Promise<void> {
  try {
    if (!('caches' in window)) {
      console.warn('⚠️  Cache API not supported');
      return;
    }

    const cacheNames = await caches.keys();
    const deletePromises = cacheNames.map((name) => caches.delete(name));

    await Promise.all(deletePromises);
  } catch (error) {
    console.error('❌ Failed to clear caches:', error);
  }
}

/**
 * Clear all cookies
 */
export function clearCookies(): void {
  try {
    const cookies = document.cookie.split(';');

    cookies.forEach((cookie) => {
      const name = cookie.split('=')[0].trim();
      // Set expiry to past date to delete
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      // Try with different path variations
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
    });

  } catch (error) {
    console.error('❌ Failed to clear cookies:', error);
  }
}

/**
 * Complete browser cleanup - clears ALL storage
 *
 * This includes:
 * - localStorage
 * - sessionStorage
 * - IndexedDB
 * - Service Workers
 * - Cache API
 * - Cookies
 */
export async function cleanupBrowser(): Promise<void> {
  // Run all cleanup operations in parallel
  await Promise.all([
    Promise.resolve(clearLocalStorage()),
    Promise.resolve(clearSessionStorage()),
    clearIndexedDB(),
    unregisterServiceWorkers(),
    clearCaches(),
    Promise.resolve(clearCookies()),
  ]);
}

/**
 * Cleanup on logout - clears all user data
 *
 * Should be called when user logs out to ensure
 * no data persists for the next session.
 */
export async function cleanupOnLogout(): Promise<void> {
  await cleanupBrowser();
}

